<template>
    <template v-if="isSwiper">
        <Swiper tag="div" class=" row-cols-2 row-cols-md-3 row-cols-lg-4 cards-container snap">
            <template #content v-if="dataSet.length > 0">
                <Card v-for="(data, index) in dataSet" :card_data="data" :basic="parent" :key="index"
                    :stats_right="data.statsRight" :stats_left="data.statsLeft" class="col border-0" :dropdown="false">
                </Card>
            </template>
        </Swiper>
    </template>

    <template v-else>
        <div class="row row-cols-2 row-cols-md-3 row-cols-lg-4 cards-container" v-if="dataSet.length > 0">
            <Card v-for="(data, index) in dataSet" :card_data="data" :index="index" :basic="parent" :key="index" :stats_right="data.statsRight"
                :stats_left="data.statsLeft" @edit-modal-open="openEditModal" class="col border-0" :dropdown="true">
            </Card>
        </div>
        <div v-if="!contentLoading && dataSet.length === 0"><p class="mb-1">Sorry, no results were found for the selected filters.</p><small>Try adjusting your filters or resetting them to see more options.</small></div>
        <div ref="infiniteLoaderContainer" class="d-none infinite-loader-container"></div>
    </template>

	<!-- Edit About Modal -->
	<div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
		<div class="modal-dialog modal-dialog-centered popOutModal">
			<div class="modal-content" @click.stop>
				<div class="modal-body">
					<textarea class="form-control" rows="6" v-model="editableAbout" placeholder="Enter description..."></textarea>
				</div>
				<div class="modal-footer pt-0 border-0">
					<button class="btn btn-outline-secondary" @click="closeEditModal" :disabled="saving">Cancel</button>
					<button class="btn btn-dark" @click="saveAbout" :disabled="saving">
						<span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
						Save
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import Card from './Index.vue';
import { ref, computed, watch } from 'vue';
import { onMounted, onUnmounted } from 'vue';
import { useWindowSize } from '@vueuse/core'
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import { useAppHelpers } from '@/composables/useAppHelpers';
import Swiper from "../Swiper.vue";
import Modal from 'bootstrap/js/dist/modal';
import { useToast } from "@/composables/useToast.js";


const props = defineProps({
    url: String,
    filters: Object,
    parent: String,
    page: String,
    specificType: String,
    isSwiper: Boolean
});

const helper = useAppHelpers();
const { displayToast } = useToast();

const infiniteLoaderContainer = ref(null);
const contentLoading = ref(false);
const { width } = useWindowSize();

const statsLeft = ref([]);
const statsRight = ref([]);

const dataSet = ref([]);
const allContentLoaded = ref(false);
const params = ref(props.filters ? props.filters : {});
// Concurrency control: track latest request and abort previous
const activeRequestId = ref(0);
let abortController = null;

const itemsToLoad = computed(() => {
    // If Swiper mode, load all
    if (props.isSwiper === true)
        return 20; //Random value for testing

    // 5 per row
    if (width.value > 1199 && width.value < 1400)
        return 25 - (dataSet.value.length % 5)
    // 7 per row
    if (width.value > 1999 && width.value < 2500)
        return 21 - (dataSet.value.length % 7)
    // 9 per row
    if (width.value > 2699 && width.value < 3000)
        return 27 - (dataSet.value.length % 9)
    //2 3 4 6 8 = 24 LCM
    return 24 - (dataSet.value.length % 24)
});

const infiniteContentLoadHandler = () => {
    if (!contentLoading.value && !allContentLoaded.value && window.scrollY + window.innerHeight >= document.documentElement.scrollHeight) {
        contentLoading.value = true;
        if (props.isSwiper === false)
            helper.loaderOn(infiniteLoaderContainer.value, true);
        loadContent('infiniteContentLoadHandler');
    }
}

const editableAbout = ref('');
const modalId = ref('editAboutModal');
const modalInstance = ref(null);
const editSlug = ref('');
const editIndex = ref(0);
const saving = ref(false);
const openEditModal = (data) => {
	editableAbout.value = data.about || '';
	editSlug.value = data.slug;
	editIndex.value = data.index;
	const el = document.getElementById(modalId.value);
	modalInstance.value = Modal.getOrCreateInstance(el);
	modalInstance.value.show();
};

const closeEditModal = () => {
	if (modalInstance.value) {
		modalInstance.value.hide();
	}
};

const saveAbout = async () => {
	try {
		saving.value = true;
		const endpoint = `${props.parent}/${editSlug.value}`;
		const resp = await apiClient.put(endpoint, { about: editableAbout.value });
		if (resp.status >= 200 && resp.status < 300) {
			dataSet.value[editIndex.value].about = editableAbout.value;
			displayToast('Description has been successfully updated.', 'outline-success');
			closeEditModal();
		}
	} catch (error) {
		await useHttpErrorManager().handleError(error, true);
	} finally {
		saving.value = false;
	}
};

const loadContent = async (location) => {
	if (!props.url) return;

	// Increment request id and cancel any previous in-flight request
	const myId = ++activeRequestId.value;
	if (abortController) {
		try { abortController.abort(); } catch (e) {}
	}
	abortController = new AbortController();

	try {
		// Use a shallow copy to avoid mutating shared params while a request is in-flight
		const localParams = { ...params.value };
		localParams['limit'] = itemsToLoad.value;
		localParams['offset'] = dataSet.value.length;

		const content = await apiClient.get(props.url, { params: localParams, signal: abortController.signal });

		// Ignore if a newer request has started
		if (myId !== activeRequestId.value) return;

		if (content.data.data) {
			let contentLoaded = content.data.data.length;
			if (contentLoaded === 0 || contentLoaded < itemsToLoad.value) {
				allContentLoaded.value = true;
			}
			content.data.data.forEach((d) => {
				if (!dataSet.value.some(item => item.id === d.id)) {
					dataSet.value.push(d);
				}
			});
		}

		setTimeout(() => {
			// Only the latest request should flip loaders/state
			if (myId !== activeRequestId.value) return;
			if (props.isSwiper === false){
				helper.loaderOff(infiniteLoaderContainer.value, true);
			}
			contentLoading.value = false;
		}, 1000);
	}
	catch (error) {
		// Ignore cancellation errors
		if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.message === 'canceled') {
			return;
		}
		setTimeout(() => {
			if (myId !== activeRequestId.value) return;
			if (props.isSwiper === false)
				helper.loaderOff(infiniteLoaderContainer.value, true);

			contentLoading.value = false;
		}, 1000);
		let errorObj = await useHttpErrorManager().handleError(error, false);
	}
}

watch(() => props.url, (val) => {
	if (val) {
		params.value = props.filters ? props.filters : {};
		dataSet.value = [];
		contentLoading.value = true;
		allContentLoaded.value = false;

		if (props.isSwiper === false)
			helper.loaderOn(infiniteLoaderContainer.value, true);
		loadContent('props.url watcher');
	}
}, { immediate: true });

watch(() => props.filters, (val) => {
    params.value = val;
    allContentLoaded.value = false;
    if (!contentLoading.value && !allContentLoaded.value) {
        dataSet.value = [];
        contentLoading.value = true;
        if (props.isSwiper === false)
            helper.loaderOn(infiniteLoaderContainer.value, true);
        loadContent('props.filters watcher');
    }
});

onMounted(() => {
    if (props.isSwiper === false) {
        window.addEventListener('scroll', infiniteContentLoadHandler)
        helper.loaderOn(infiniteLoaderContainer.value, true);
    }

    contentLoading.value = true;
    setStatsArray(props.page, props.parent);
    // loadContent('onMounted');
})

onUnmounted(() => {
    if (props.isSwiper === false)
        window.removeEventListener('scroll', infiniteContentLoadHandler);
});

const setStatsArray = (page, parent) => {
    if (page === 'basic') {
        if (parent === 'medications') {
            statsLeft.value = [
                { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '123 People', containerClass: '' }
            ];
            statsRight.value = [
                { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
            ];
        }
        if (parent === 'conditions') {
            statsLeft.value = [
                { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: 0, tooltipText: '257 People', containerClass: '' }
            ];
            statsRight.value = [
                { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
            ];
        }
        if (parent === 'therapies') {
            statsLeft.value = [
                { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '123 People', containerClass: '' }
            ];
            statsRight.value = [
                { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
            ];
        }
        if (parent === 'side_effects') {
            statsLeft.value = [
                { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '123 People', containerClass: '' }
            ];
            statsRight.value = [
                { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
            ];
        }
        if (parent === 'symptoms') {
            statsLeft.value = [
                { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '123 People', containerClass: '' }
            ];
            statsRight.value = [
                { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
            ];
        }
        if (parent === 'categories') {
            statsLeft.value = [
                { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '123 People', containerClass: '' }
            ];
            statsRight.value = [
                { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
            ];
        }
    }
    if (page === 'specific') {
        if (props.specificType === 'medication') {
            if (parent === 'conditions') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
            if (parent === 'therapies') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
            if (parent === 'side_effects') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
            if (parent === 'categories') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
        }

        if (props.specificType === 'condition') {
            if (parent === 'medications') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
            if (parent === 'symptoms') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
        }

        if (props.specificType === 'side_effect') {
            if (parent === 'medications') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
        }

        if (props.specificType === 'symptom') {
            if (parent === 'conditions') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
        }

        if (props.specificType === 'category') {
            if (parent === 'medications') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
            if (parent === 'side_effects') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
        }

        if (props.specificType === 'therapy') {
            if (parent === 'medications') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-star-fill', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
            if (parent === 'side_effects') {
                statsLeft.value = [
                    { iconType: 'bootstrap', icon: 'bi-people', iconColor: 'text-gray-600', value: '6.4%', extraValue: '', tooltipText: '257 People', containerClass: '' }
                ];
                statsRight.value = [
                    { iconType: 'bootstrap', icon: 'bi-fire', iconColor: 'text-warning', value: 9.7, tooltipText: '123 Ratings', containerClass: 'fw-semi-bold' }
                ];
            }
        }
    }
}

</script>

// Ensure any in-flight request is cancelled when component unmounts
onUnmounted(() => {
	if (abortController) {
		try { abortController.abort(); } catch (e) {}
	}
});
